/**
 * View Store - Manages active view state (Beats vs References)
 *
 * This store controls which view is currently visible in the Node View area.
 * Uses Zustand persist middleware to remember the last active view.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type Viewport } from '@xyflow/react';

/**
 * View type - which canvas view is currently active
 */
export type ViewType = 'beats' | 'references';

/**
 * View store state and actions
 */
interface ViewStore {
  /** Current active view */
  activeView: ViewType;

  /** Whether to automatically switch to beats view after sending reference as beat */
  autoSwitchViewAfterSend: boolean;

  /** Viewport state for beats view (zoom & pan) */
  beatsViewport: Viewport;

  /** Viewport state for references view (zoom & pan) */
  referencesViewport: Viewport;

  /** Last selected beat node IDs (persisted when switching away from beats view) */
  lastSelectedBeatIds: string[];

  /** Last selected reference node IDs (persisted when switching away from references view) */
  lastSelectedReferenceIds: string[];

  /** Switch between views */
  setActiveView: (view: ViewType) => void;

  /** Toggle auto-switch preference */
  setAutoSwitchViewAfterSend: (value: boolean) => void;

  /** Save viewport for beats view */
  setBeatsViewport: (viewport: Viewport) => void;

  /** Save viewport for references view */
  setReferencesViewport: (viewport: Viewport) => void;

  /** Save last selected beat IDs (called when switching away from beats view) */
  setLastSelectedBeatIds: (ids: string[]) => void;

  /** Save last selected reference IDs (called when switching away from references view) */
  setLastSelectedReferenceIds: (ids: string[]) => void;
}

/**
 * View store instance
 * Persisted to localStorage to remember last view across sessions
 */
export const useViewStore = create<ViewStore>()(
  persist(
    (set) => ({
      activeView: 'beats', // Default to beats view
      autoSwitchViewAfterSend: false, // Default to auto-switch disabled
      beatsViewport: { x: 0, y: 0, zoom: 1 }, // Default viewport
      referencesViewport: { x: 0, y: 0, zoom: 1 }, // Default viewport
      lastSelectedBeatIds: [], // No selection by default
      lastSelectedReferenceIds: [], // No selection by default

      setActiveView: (view) => set({ activeView: view }),

      setAutoSwitchViewAfterSend: (value) => set({ autoSwitchViewAfterSend: value }),

      setBeatsViewport: (viewport) => set({ beatsViewport: viewport }),

      setReferencesViewport: (viewport) => set({ referencesViewport: viewport }),

      setLastSelectedBeatIds: (ids) => set({ lastSelectedBeatIds: ids }),

      setLastSelectedReferenceIds: (ids) => set({ lastSelectedReferenceIds: ids }),
    }),
    {
      name: 'nodebeat-view-store', // localStorage key
    }
  )
);
